<template>
  <div
    class="account-list"
    :class="[`type-${type}`, platform]"
    data-t="account-list"
  >
    <p class="account-title">{{ title }}</p>
    <div class="account-list-wrapper">
      <div
        v-for="account in accounts"
        :key="account.id"
        class="account"
        :class="{
          disabled: !account.isAvailable,
          active: account.code === activeAccount?.code,
        }"
        data-t="account"
        :data-is-active="account.code === activeAccount?.code"
        :data-currency-code="account.code"
        @click="setNewActiveAccount(account.id)"
      >
        <div class="wrapper-left">
          <div class="radio" data-t="currency-radio-button-r34c" />
          <div class="currency-logo">
            <StIcon :name="account.icon" :size="iconSize" />
          </div>
        </div>
        <div class="wrapper-right">
          <div class="currency-content">
            <div class="short-name" data-t="short-name">
              {{ account.code }}
            </div>
            <div class="full-name" data-t="full-name">
              {{ getAccountName(account) }}
            </div>
          </div>
          <div class="balance-crypto-wrapper">
            <div class="balance-crypto" data-t="balance-crypto">
              {{ formatCrypto(account.balance) }}
            </div>
            <BonusBalance
              v-if="+account.bonusAccountAmount > 0"
              :amount="account.bonusAccountAmount"
            />
            <div v-else class="balance-fiat" data-t="balance-fiat">
              {{ formatCurrency(account.fiatBalance) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAccountsStore } from '../../../../stores/useAccountsStore'
import { useCurrenciesStore } from '../../../../stores/useCurrenciesStore'

const emit = defineEmits<{
  accountSelect: []
}>()

const { title, accounts, type } = defineProps<{
  title: string
  accounts: ExtendedUserAccount[]
  type: 'header' | 'wallet' | 'widget'
}>()

const { t } = useI18n()
const { platform, isMobile } = usePlatform()
const accountStore = useAccountsStore()
const { updateActiveAccount } = accountStore
const { activeAccount } = storeToRefs(accountStore)

const currenciesStore = useCurrenciesStore()
const { appCurrency } = storeToRefs(currenciesStore)

const { format: formatCrypto } = useCryptoFormatter()
const { format: formatCurrency } = useCurrencyFormatter({
  currency: appCurrency.value.code,
})

function setNewActiveAccount(accountId: number): void {
  updateActiveAccount(accountId)
  emit('accountSelect')
}

function getAccountName(account: ExtendedUserAccount): string {
  return account.isAvailable
    ? account.name
    : `${account.name} (${t('payments.siteAvailableCrypto.siteOnly')})`
}

const iconSize = computed(() =>
  type === 'wallet' && !isMobile.value ? 32 : 24,
)
</script>

<style scoped>
.account-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
}

.account-title {
  margin: 0;
  font: var(--desktop-text-sm-medium);
  color: var(--text-secondary);
}

.account-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-050);
}

.wrapper-left,
.wrapper-right,
.currency-header-left,
.currency-header-right {
  display: flex;
  gap: var(--spacing-200);
  align-items: center;
  height: 100%;
}

.wrapper-right {
  flex-grow: 1;
  justify-content: space-between;
}

.radio {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: var(--spacing-200);
  height: var(--spacing-200);

  border: 1.5px solid var(--border-primary);
  border-radius: var(--border-radius-full);

  transition: all 0.2s ease;

  .active & {
    border-color: var(--text-brand);
  }

  .active &::after {
    content: '';

    width: 10px;
    height: 10px;

    background-color: var(--text-brand);
    border-radius: var(--border-radius-full);
  }
}

.currency-logo {
  line-height: 0;
}

.currency-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: var(--border-radius-050);
}

.account {
  cursor: pointer;

  display: flex;
  gap: var(--spacing-150);
  align-items: center;

  height: 56px;

  border-radius: var(--border-radius-100);

  transition: all 0.2s ease;

  &:hover {
    background-color: var(--background-tertiary);

    .radio {
      border-color: var(--text-brand);
    }
  }

  &.disabled {
    pointer-events: none;

    & > * {
      opacity: 0.4;
    }
  }
}

.short-name {
  font: var(--desktop-text-md-semibold);
}

.full-name {
  font: var(--desktop-text-xs-medium);
  color: var(--text-tertiary);
}

.balance-crypto-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.balance-crypto {
  font: var(--desktop-text-md-medium);
}

.balance-fiat {
  font: var(--desktop-text-xs-medium);
  color: var(--text-tertiary);
}

.type-header {
  .account-list {
    gap: var(--spacing-050);
  }

  .account {
    height: 48px;
    padding: 0 var(--spacing-150);
    border-radius: var(--border-radius-075);
  }

  .account-title {
    padding: var(--spacing-100) var(--spacing-150) 0;
  }

  .wrapper-left,
  .wrapper-right {
    gap: var(--spacing-150);
  }
}

.mobile {
  .account-list {
    gap: 0;
  }

  .account {
    gap: var(--spacing-100);
    height: 48px;
    background-color: transparent;
  }

  .account-list-wrapper {
    gap: 0;
  }

  .short-name {
    font: var(--mobile-text-medium);
  }

  .full-name {
    font: var(--mobile-caption-1-regular);
  }

  .currency-content {
    gap: var(--spacing-025);
  }

  .balance-crypto-wrapper {
    gap: var(--spacing-025);
  }

  .balance-crypto {
    font: var(--mobile-text-medium);
  }

  .balance-fiat {
    font: var(--mobile-caption-1-regular);
  }
}

.type-widget {
  .account {
    height: 52px;

    &:hover {
      background-color: transparent;
    }
  }

  .wrapper-left {
    gap: var(--spacing-200);
  }
}

.desktop.type-wallet {
  .account {
    gap: var(--spacing-200);
    padding: 0 var(--spacing-200);
    background-color: var(--background-primary);

    &:hover {
      background-color: var(--background-secondary);
    }
  }

  & .account.active {
    border: 1px solid var(--text-brand);
  }
}
</style>
